import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Valor } from '../../components/Valor';
import { useState } from 'react';
import { categoryColor } from '../../components/CategoryTag';
import { ApiError, api, type Asset, type AssetKind } from '../../lib/api';
import { dateBR, money, parseAmountInput, today } from '../../lib/format';

/**
 * Patrimonio.
 *
 * Um numero grande e a composicao dele. A composicao existe porque o total
 * sozinho engana: cem mil em conta e cem mil num apartamento sao a mesma
 * linha num resumo e coisas completamente diferentes na vida.
 *
 * As dividas aparecem como barras negativas, e nao somem numa subtracao
 * silenciosa. Uma fatura de cartao de tres mil reais tem de ser visivel
 * quando se olha "quanto eu tenho".
 *
 * O sistema nao estima valor de nada: nao consulta FIPE nem tabela de IPTU.
 * O numero e o que voce informou, e a tela diz de quando ele e — um imovel
 * avaliado ha tres anos nao vale hoje o que dizia entao, e esconder a data
 * seria fingir precisao que nao existe.
 */

const KIND_LABEL: Record<AssetKind, string> = {
  REAL_ESTATE: 'Imovel',
  VEHICLE: 'Veiculo',
  INVESTMENT: 'Investimento',
  RECEIVABLE: 'A receber',
  OTHER: 'Outro',
};

export function PatrimonioPage() {
  const cliente = useQueryClient();
  const [novoAberto, setNovoAberto] = useState(false);
  const [reavaliando, setReavaliando] = useState<Asset | null>(null);

  const patrimonio = useQuery({ queryKey: ['patrimonio'], queryFn: () => api.netWorth() });
  const bens = useQuery({ queryKey: ['bens'], queryFn: () => api.assets() });

  const recarregar = () => {
    void cliente.invalidateQueries({ queryKey: ['patrimonio'] });
    void cliente.invalidateQueries({ queryKey: ['bens'] });
    void cliente.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const total = patrimonio.data;

  return (
    <div className="main">
      <header className="page-header">
        <div className="between">
          <div>
            <h1>Patrimonio</h1>
            <p>Tudo o que voce tem, menos tudo o que voce deve.</p>
          </div>
          <button className="btn" onClick={() => setNovoAberto((aberto) => !aberto)}>
            {novoAberto ? 'Cancelar' : 'Cadastrar bem'}
          </button>
        </div>
      </header>

      {novoAberto && (
        <FormularioDeBem
          onPronto={() => {
            setNovoAberto(false);
            recarregar();
          }}
        />
      )}

      {total && (
        <section className="card">
          <div className="stat-label">Patrimonio liquido</div>
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <Valor cents={total.totalCents} className="stat-value" />
          </div>

          {total.composition.length === 0 ? (
            <p className="muted">
              Ainda nao ha nada somando. Cadastre um bem, ou conecte um banco em Conexoes.
            </p>
          ) : (
            <div className="stack" style={{ gap: 'var(--space-3)' }}>
              {total.composition.map((parcela) => {
                return (
                  <div key={parcela.key}>
                    <div className="between" style={{ marginBottom: 4 }}>
                      <span className="cat-tag" style={{ background: 'transparent', padding: 0 }}>
                        <span
                          className="cat-dot"
                          style={{ background: categoryColor(parcela.colorToken) }}
                          aria-hidden
                        />
                        {parcela.label}
                      </span>
                      <Valor cents={parcela.amountCents} className="tabular" />
                    </div>
                    <div className="share-bar">
                      <span
                        style={{
                          width: `${Math.min(parcela.share, 100)}%`,
                          background: categoryColor(parcela.colorToken),
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <section className="card" style={{ marginTop: 'var(--space-6)' }}>
        <h2 className="card-title">Bens</h2>
        <p className="card-hint">
          O que nao esta em banco: imoveis, veiculos e o que mais tiver valor. O valor e o que voce
          informou, com a data em que informou.
        </p>

        {bens.isLoading && <p className="muted">Carregando...</p>}

        {bens.data && bens.data.assets.length === 0 && (
          <p className="muted">Nenhum bem cadastrado ainda.</p>
        )}

        {bens.data && bens.data.assets.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Bem</th>
                  <th>Tipo</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th style={{ textAlign: 'right' }}>Falta pagar</th>
                  <th style={{ textAlign: 'right' }}>Vale para voce</th>
                  <th>Avaliado em</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bens.data.assets.map((bem) => (
                  <tr key={bem.id}>
                    <td>
                      <span className="payee-name">
                        <span>{bem.name}</span>
                        {!bem.includeInNetWorth && (
                          <span className="payee-meta">fora do patrimonio</span>
                        )}
                      </span>
                    </td>
                    <td className="muted">{KIND_LABEL[bem.kind]}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Valor cents={bem.valueCents} />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Valor cents={bem.debtCents} saida />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Valor cents={bem.netCents} />
                    </td>
                    <td className="tabular muted">
                      {dateBR(bem.valuedOn)}
                      {bem.valuationCount > 1 && (
                        <span className="payee-meta"> · {bem.valuationCount} avaliacoes</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn ghost" onClick={() => setReavaliando(bem)}>
                        Atualizar valor
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {reavaliando && (
        <FormularioDeAvaliacao
          bem={reavaliando}
          onFechar={() => setReavaliando(null)}
          onPronto={() => {
            setReavaliando(null);
            recarregar();
          }}
        />
      )}
    </div>
  );
}

function FormularioDeBem({ onPronto }: { onPronto: () => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<AssetKind>('REAL_ESTATE');
  const [valor, setValor] = useState('');
  const [divida, setDivida] = useState('');
  const [valuedOn, setValuedOn] = useState(today());
  const [erro, setErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () =>
      api.createAsset({
        name: name.trim(),
        kind,
        valueCents: parseAmountInput(valor).toString(),
        // Vazio e zero, nao "nao informado": um bem sem financiamento tem
        // divida zero, e mandar undefined deixaria o campo sem significado.
        debtCents: divida.trim() === '' ? '0' : parseAmountInput(divida).toString(),
        valuedOn,
      }),
    onSuccess: onPronto,
    onError: (falha) =>
      setErro(falha instanceof ApiError ? falha.message : 'Nao consegui cadastrar.'),
  });

  return (
    <section className="card">
      <h2 className="card-title">Novo bem</h2>
      <form
        className="stack"
        onSubmit={(evento) => {
          evento.preventDefault();
          setErro(null);
          criar.mutate();
        }}
      >
        <label>
          Nome
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
        </label>

        <label>
          Tipo
          <select value={kind} onChange={(e) => setKind(e.target.value as AssetKind)}>
            {Object.entries(KIND_LABEL).map(([valor, rotulo]) => (
              <option key={valor} value={valor}>
                {rotulo}
              </option>
            ))}
          </select>
        </label>

        <label>
          Quanto vale hoje
          <input
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            inputMode="decimal"
            placeholder="400000,00"
            required
          />
        </label>

        <label>
          Quanto ainda falta pagar (financiamento)
          <input
            value={divida}
            onChange={(e) => setDivida(e.target.value)}
            inputMode="decimal"
            placeholder="0,00"
          />
        </label>

        <label>
          Data desta avaliacao
          <input type="date" value={valuedOn} onChange={(e) => setValuedOn(e.target.value)} />
        </label>

        {erro && <p className="expense">{erro}</p>}

        <div className="row">
          <button className="btn" type="submit" disabled={criar.isPending}>
            {criar.isPending ? 'Cadastrando...' : 'Cadastrar'}
          </button>
        </div>
      </form>
    </section>
  );
}

function FormularioDeAvaliacao({
  bem,
  onFechar,
  onPronto,
}: {
  bem: Asset;
  onFechar: () => void;
  onPronto: () => void;
}) {
  const [valor, setValor] = useState('');
  const [divida, setDivida] = useState('');
  const [source, setSource] = useState('');
  const [valuedOn, setValuedOn] = useState(today());
  const [erro, setErro] = useState<string | null>(null);

  const historico = useQuery({
    queryKey: ['bem-avaliacoes', bem.id],
    queryFn: () => api.assetValuations(bem.id),
  });

  const salvar = useMutation({
    mutationFn: () =>
      api.revalueAsset(bem.id, {
        valueCents: parseAmountInput(valor).toString(),
        // Campo em branco mantem a divida atual: quem esta corrigindo o valor
        // do imovel nao esta dizendo que quitou o financiamento.
        ...(divida.trim() === '' ? {} : { debtCents: parseAmountInput(divida).toString() }),
        valuedOn,
        source: source.trim() === '' ? null : source.trim(),
      }),
    onSuccess: onPronto,
    onError: (falha) =>
      setErro(falha instanceof ApiError ? falha.message : 'Nao consegui atualizar.'),
  });

  return (
    <section className="card" style={{ marginTop: 'var(--space-6)' }}>
      <div className="between">
        <h2 className="card-title">Atualizar o valor de {bem.name}</h2>
        <button className="btn ghost" onClick={onFechar}>
          Fechar
        </button>
      </div>
      <p className="card-hint">
        O valor anterior nao e apagado: fica no historico, com a data. E assim que da para ver
        depois se o bem subiu ou caiu.
      </p>

      <form
        className="stack"
        onSubmit={(evento) => {
          evento.preventDefault();
          setErro(null);
          salvar.mutate();
        }}
      >
        <label>
          Novo valor
          <input
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            inputMode="decimal"
            required
          />
        </label>

        <label>
          Falta pagar (em branco mantem {money(bem.debtCents)})
          <input value={divida} onChange={(e) => setDivida(e.target.value)} inputMode="decimal" />
        </label>

        <label>
          De onde saiu o numero
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="avaliacao de corretor, tabela FIPE, IPTU..."
            maxLength={120}
          />
        </label>

        <label>
          Data
          <input type="date" value={valuedOn} onChange={(e) => setValuedOn(e.target.value)} />
        </label>

        {erro && <p className="expense">{erro}</p>}

        <div className="row">
          <button className="btn" type="submit" disabled={salvar.isPending}>
            {salvar.isPending ? 'Salvando...' : 'Salvar avaliacao'}
          </button>
        </div>
      </form>

      {historico.data && historico.data.length > 0 && (
        <div className="table-scroll" style={{ marginTop: 'var(--space-4)' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Data</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
                <th style={{ textAlign: 'right' }}>Falta pagar</th>
                <th>Fonte</th>
              </tr>
            </thead>
            <tbody>
              {historico.data.map((avaliacao) => (
                <tr key={avaliacao.id}>
                  <td className="tabular">{dateBR(avaliacao.valuedOn)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Valor cents={avaliacao.valueCents} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Valor cents={avaliacao.debtCents} saida />
                  </td>
                  <td className="muted">{avaliacao.source ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
